import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import {
  faSearch, faPlus, faEdit, faTrash, faTimes, faCheck,
  faQuestionCircle, faCog, faArrowLeft, faSpinner,
  faExclamationTriangle, faUser, faCopy
} from '@fortawesome/free-solid-svg-icons';
import { ConfigurationsService, Configuration } from '../../services/configurations.service';
import { TenantService } from '../../services/tenant.service';
import { AlertService } from '../../components/alert-service/alert-service';
import { DateFormatterService } from '../../services/date-formatter.service';
import { ItemListComponent, ListItem, ListConfig } from '../../components/item-list/item-list.component';
import { HeaderActionsComponent, HeaderActionsConfig } from '../../components/header-actions/header-actions.component';
import { ModalDeleteConfirmComponent } from '../../components/modal-delete-confirm/modal-delete-confirm.component';
import { ModalDetailsComponent } from '../../components/modal-details/modal-details.component';
import { normalizeForSearch } from '../../utils/text-search.util';
import { ModalCreateEditComponent } from '../../components/modal-creat-edit-config/modal-create-edit.component';

@Component({
  selector: 'app-configurations',
  standalone: true,
  imports: [
    FormsModule,
    RouterModule,
    FontAwesomeModule,
    TranslateModule,
    ItemListComponent,
    HeaderActionsComponent,
    ModalDeleteConfirmComponent,
    ModalDetailsComponent,
    ModalCreateEditComponent
],
  templateUrl: './configurations.component.html',
  styleUrls: ['./configurations.component.scss']
})
export class ConfigurationsComponent implements OnInit, OnDestroy {
  configurations: Configuration[] = [];
  filteredConfigurations: Configuration[] = [];
  itemsForList: ListItem[] = [];
  searchTerm = '';
  loading = false;

  showCreateModal = false;
  showEditModal = false;
  showDeleteModal = false;
  showDetailsModal = false;

  detailsTab: 'details' | 'users' = 'details';

  selectedConfiguration: Configuration | null = null;
  
  configType: 'delfos_server' | 'delfos_llm' | 'di2winocr_server' | 'di2winocr_features' | 'retriever' = 'delfos_server';

  private configSubscription?: Subscription;

  headerConfig: HeaderActionsConfig = {
    addButton: {
      type: 'add',
      text: 'BUTTON.CREATE.CONFIGURATION',
      icon: faPlus,
      visible: true,
      disabled: false
    },
    helpButton: {
      type: 'help',
      icon: faQuestionCircle,
      visible: false,
      disabled: true
    },
    searchConfig: {
      showTextSearch: true,
      showTagFilter: false,
      textPlaceholder: 'SEARCH.PLACEHOLDER.CONFIGURATIONS',
      tagPlaceholder: ''
    },
    showBackButton: true
  };

  listConfig: ListConfig = {
    itemIcon: faCog,
    itemIconClass: 'config-icon',
    showMetadata: true,
    showType: true,
    typeLabels: {
      delfos_server: 'delfos_server',
      delfos_llm: 'delfos_llm',
      di2winocr_server: 'di2winocr_server',
      di2winocr_features: 'di2winocr_features',
      retriever: 'retriever'
    },
    emptyMessage: 'MESSAGE.NO.CONFIGURATIONS',
    emptyMessageFiltered: 'MESSAGE.NO.CONFIGURATIONS.SEARCH',
    actions: [
      { id: 'edit', label: 'BUTTON.EDIT', icon: faEdit, cssClass: 'edit-item' },
      { id: 'duplicate', label: 'BUTTON.DUPLICATE', icon: faCopy, cssClass: 'duplicate-item' },
      { id: 'delete', label: 'BUTTON.DELETE', icon: faTrash, cssClass: 'delete-item' }
    ]
  };

  faSearch = faSearch;
  faPlus = faPlus;
  faEdit = faEdit;
  faTrash = faTrash;
  faTimes = faTimes;
  faCheck = faCheck;
  faQuestionCircle = faQuestionCircle;
  faCog = faCog;
  faArrowLeft = faArrowLeft;
  faSpinner = faSpinner;
  faExclamationTriangle = faExclamationTriangle;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private configService: ConfigurationsService,
    private alertService: AlertService,
    private dateFormatter: DateFormatterService,
    private tenantService: TenantService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      if (params['type']) {
        this.configType = params['type'];
      }
      this.loadConfigurations();
    });
  }

  ngOnDestroy(): void {
    this.configSubscription?.unsubscribe();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(_: Event): void {}

  loadConfigurations(): void {
    this.loading = true;
    this.configService.list(this.configType).subscribe({
      next: (response) => {
        this.configurations = response.configurations || [];
        this.applyFilters();
        this.loading = false;
      },
      error: (err) => {
        this.alertService.showError(err);
        this.loading = false;
        this.configurations = [];
        this.filteredConfigurations = [];
        this.itemsForList = [];
      }
    });
  }

  onSearch(): void {
    this.applyFilters();
  }

  applyFilters(): void {
    const term = normalizeForSearch(this.searchTerm);
    if (!term) {
      this.filteredConfigurations = [...this.configurations];
    } else {
      this.filteredConfigurations = this.configurations.filter(c =>
        normalizeForSearch(c['name']).includes(term) ||
        normalizeForSearch(c['key']).includes(term) ||
        normalizeForSearch(c.type).includes(term)
      );
    }
    this.buildItemsForList();
  }

  private buildItemsForList(): void {
    this.itemsForList = this.filteredConfigurations.map(c => ({
      id: c.id,
      name: c.name || c.key,
      created_date: c['created_at'] ? this.dateFormatter.formatDate(c['created_at']) : '',
      created_time: c['created_at'] ? this.dateFormatter.formatTime(c['created_at']) : '',
      type: c.type,
      tag: null
    }));
  }

  onItemClick(item: ListItem): void {
    const config = this.configurations.find(c => c.id === item.id);
    if (config) this.openDetailsModal(config);
  }

  onActionClick(event: { action: string; item: ListItem; event: Event }): void {
    const config = this.configurations.find(c => c.id === event.item.id);
    if (!config) return;
    if (event.action === 'edit') this.openEditModal(config);
    if (event.action === 'duplicate') this.duplicateConfiguration(config);
    if (event.action === 'delete') this.openDeleteModal(config);
  }

  duplicateConfiguration(config: Configuration): void {
    const payload = this.buildCreatePayloadFromConfig(config);
    this.configService.create(config.type, payload).subscribe({
      next: () => {
        this.loadConfigurations();
        this.alertService.showSuccess(this.translate.instant('CONFIGURATIONS.CREATE.SUCCESS'));
      },
      error: (err) => this.alertService.showError(err)
    });
  }

  private nextDuplicateName(baseKey: string): string {
    const existing = new Set(this.configurations.map(c => c.key));
    let n = 1;
    while (existing.has(`${baseKey} (${n})`)) {
      n++;
    }
    return `${baseKey} (${n})`;
  }

  private buildCreatePayloadFromConfig(config: Configuration): any {
    const name = this.nextDuplicateName(config.key);
    const value = config.value || {};

    if (config.type === 'delfos_server' || config.type === 'di2winocr_server') {
      return { name, url: value.url ?? '' };
    }
    if (config.type === 'delfos_llm') {
      return { name, llm_model: value.llm_model ?? '' };
    }
    if (config.type === 'retriever') {
      return { name, retriever: value.retriever ?? '' };
    }
    if (config.type === 'di2winocr_features') {
      return {
        name,
        display_name: config.name || name,
        layoutAnalysis: value.layoutAnalysis ?? '',
        inputType: value.inputType ?? '',
        ocrType: value.ocrType ?? '',
        outputType: value.outputType ?? '',
        textType: value.textType ?? 'mix',
        ocrCrop: value.ocrCrop ?? false,
        autoCut: value.autoCut ?? false,
        enhanceDocument: value.enhanceDocument ?? false,
        sortOcr: value.sortOcr ?? false,
        adjustRotation: typeof value.adjustRotation === 'string' ? value.adjustRotation : '',
        splitPdf: value.splitPdf ?? false
      };
    }
    return { name, ...value };
  }

  openDetailsModal(config: Configuration): void {
    this.selectedConfiguration = config;
    this.detailsTab = 'details';
    this.showDetailsModal = true;
  }

  openCreateModal(): void {
    this.showCreateModal = true;
  }

  closeDetailsModal(): void {
    this.showDetailsModal = false;
    this.selectedConfiguration = null;
  }

  openEditModal(config: Configuration): void {
    this.selectedConfiguration = config;
    this.showEditModal = true;
  }

  openDeleteModal(config: Configuration): void {
    this.selectedConfiguration = config;
    this.showDeleteModal = true;
  }

  deleteConfiguration(): void {
    if (!this.selectedConfiguration) return;
    this.configService.delete(this.selectedConfiguration.id).subscribe({
      next: () => {
        this.closeModals();
        this.loadConfigurations();
        this.alertService.showSuccess(this.translate.instant('CONFIGURATIONS.DELETE.SUCCESS'));
      },
      error: (err) => this.alertService.showError(err)
    });
  }

  closeModals(): void {
    this.showCreateModal = false;
    this.showEditModal = false;
    this.showDeleteModal = false;
    this.selectedConfiguration = null;
  }

  goBack(): void {
    this.router.navigate(this.tenantService.buildRoute('/settings'));
  }

  get hasActiveFilters(): boolean {
    return !!this.searchTerm.trim();
  }

  trackById(_: number, item: ListItem): string | number {
    return item.id;
  }

  getHeaderTitle(): string {
    const titles: Record<string, string> = {
      'delfos_server': 'SETTINGS.PREFERENCES.DELFOS_SERVER',
      'delfos_llm': 'SETTINGS.PREFERENCES.DELFOS_LLM',
      'di2winocr_server': 'SETTINGS.PREFERENCES.OCR_SERVER',
      'di2winocr_features': 'SETTINGS.PREFERENCES.OCR_FEATURES',
      'retriever': 'SETTINGS.PREFERENCES.RETRIEVER'
    };
    return titles[this.configType] || 'CONFIGURATIONS.TITLE';
  }

  getHeaderTitlePlural(): string {
    const titles: Record<string, string> = {
      'delfos_server': 'SETTINGS.PREFERENCES.DELFOS_SERVER_PLURAL',
      'delfos_llm': 'SETTINGS.PREFERENCES.DELFOS_LLM_PLURAL',
      'di2winocr_server': 'SETTINGS.PREFERENCES.OCR_SERVER_PLURAL',
      'di2winocr_features': 'SETTINGS.PREFERENCES.OCR_FEATURES_PLURAL',
      'retriever': 'SETTINGS.PREFERENCES.RETRIEVER_PLURAL'
    };
    return titles[this.configType] || 'CONFIGURATIONS.TITLE';
  }

  onHeaderFiltersChanged(event: any): void {
    if ('searchTerm' in event) {
      this.searchTerm = event.searchTerm || '';
      this.onSearch();
    }
  }
}